import { BaseApiClient } from './base-client.js'
import { API_ROUTES, type ApiResponse, type Graph } from '@super/shared-types'
import { withErrorHandling } from '../utils'

export interface DispatchAgentRequest {
  agentName: string
  roomName: string
  metadata?: Record<string, unknown>
  envPrefix?: string
}

export interface DispatchAgentResponse {
  roomName: string
  agentName: string
  dispatchId?: string
}

export interface WebDispatchRequest {
  nodeGraph: Graph
  variableValues?: Record<string, string>
  companyId?: string
  graphId?: string
  /** The signed-in user's jev feature flag. */
  jev?: boolean
}

export interface WebDispatchResponse {
  url: string
  token: string
  room: string
  identity: string
  dispatchId?: string
  sessionId?: string
}

/**
 * LiveKit API Client
 * Handles LiveKit-related API calls including agent dispatch
 */
export class LiveKitApiClient extends BaseApiClient {
  async dispatchAgent(
    dto: DispatchAgentRequest,
  ): Promise<DispatchAgentResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<DispatchAgentResponse>>(
          API_ROUTES.LIVEKIT.DISPATCH,
          {
            method: 'POST',
            body: JSON.stringify(dto),
          },
        )
        return response.data || null
      },
      { errorContext: 'LiveKitApiClient.dispatchAgent' },
    )
  }

  /**
   * Dispatch node-caller in web mode and get back the browser-side session.
   * Used by admin's "test in browser" flow on the node-agents editor.
   */
  async webDispatch(
    dto: WebDispatchRequest,
  ): Promise<WebDispatchResponse | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<WebDispatchResponse>>(
          API_ROUTES.LIVEKIT.WEB_DISPATCH,
          {
            method: 'POST',
            body: JSON.stringify(dto),
          },
        )
        return response.data || null
      },
      { errorContext: 'LiveKitApiClient.webDispatch' },
    )
  }
}

// Export singleton instance
export const livekitClient = new LiveKitApiClient()
